import React from 'react';
import {
  Activity,
  Bed,
  BriefcaseMedical,
  CalendarCheck2,
  Droplets,
  FileText,
  LayoutDashboard,
  Pill,
  Settings,
  Stethoscope,
  TestTube2,
  UserCog,
  Wallet,
} from 'lucide-react';

function ToothIcon({ size = 13, strokeWidth = 1.9 }) {
  return React.createElement(
    'svg',
    {
      width: size,
      height: size,
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      'aria-hidden': 'true',
    },
    React.createElement('path', {
      d: 'M8.4 4.2c1.1 0 2 .5 3.6.5s2.5-.5 3.6-.5c2.6 0 4.2 2 4.2 4.7 0 1.5-.4 3-1 4.3-.7 1.5-1.4 3.1-2.2 4.6-.4.8-1.6.8-1.9 0L13 14.6c-.3-.7-1.3-.7-1.6 0l-1.7 3.2c-.4.8-1.5.8-1.9 0-.8-1.5-1.5-3.1-2.2-4.6-.6-1.3-1-2.8-1-4.3 0-2.7 1.6-4.7 4.2-4.7Z',
    }),
  );
}

export const APP_SIDEBAR = [
  {
    title: 'PATIENT CARE',
    items: [
      { key: 'main', label: 'Dashboard', icon: LayoutDashboard, badge: '' },
      { key: 'opd', label: 'Outdoor Patient Department', icon: BriefcaseMedical, badge: '' },
      { key: 'emergency', label: 'Emergency', icon: Stethoscope, badge: '24' },
      { key: 'ipd', label: 'Indoor Patient Department', icon: Bed, badge: '' },
      { key: 'patient_record', label: 'Patient Records', icon: FileText, badge: '' },
    ],
  },
  {
    title: 'DIAGNOSTICS',
    items: [
      { key: 'pharmacy', label: 'Pharmacy', icon: Pill, badge: '7' },
      { key: 'pathology', label: 'Laboratory', icon: TestTube2, badge: '18' },
      { key: 'radiology', label: 'Radiology', icon: Activity, badge: '' },
      { key: 'dentistry', label: 'Dentistry', icon: ToothIcon, badge: '' },
      { key: 'blood_bank', label: 'Blood Bank', icon: Droplets, badge: '3' },
    ],
  },
  {
    title: 'CLINICAL SERVICES',
    items: [
      { key: 'operation_theatre', label: 'Operation Theatre', icon: CalendarCheck2, badge: '2' },
      { key: 'hdu_icu', label: 'ICU / HDU', icon: Stethoscope, badge: '16' },
    ],
  },
  {
    title: 'FINANCE',
    items: [
      { key: 'admin', label: 'Admin Section', icon: UserCog, badge: '', route: '/admin' },
      { key: 'cash_reconciliation', label: 'Cash Reconciliation', icon: Wallet, badge: '', route: '/cash-reconciliation' },
    ],
  },
  {
    title: 'ADMINISTRATION',
    items: [
      { key: 'admin_settings', label: 'Settings', icon: Settings, badge: '', route: '/settings' },
    ],
  },
];

export const MODULE_SUBMENUS = {
  opd: [
    { key: 'opd_overview', label: 'Overview' },
    { key: 'opd_register', label: 'Register Patient' },
    { key: 'opd_walk_in', label: 'Walk-In Patients' },
    { key: 'opd_queue', label: 'Queue Management' },
    { key: 'opd_reports', label: 'Reports' },
    { key: 'opd_cancel', label: 'Cancel Appointment' },
    { key: 'opd_bills', label: 'Print Bills' },
  ],
  emergency: [
    { key: 'emergency_mlc_policy', label: 'MLC Policy' },
    { key: 'emergency_er_protocols', label: 'Emergency Protocols' },
    { key: 'emergency_patient_record', label: 'Emergency Patient Record' },
  ],
  ipd: [
    { key: 'ipd_overview', label: 'Overview' },
    { key: 'ipd_workflow', label: 'Indoor Workflow' },
    {
      key: 'ipd_docs',
      label: 'Generate Documents',
      children: [
        { key: 'ipd_doc_discharge', label: 'Discharge Slip' },
        { key: 'ipd_doc_birth', label: 'Birth Certificate' },
        { key: 'ipd_doc_treatment', label: 'Treatment Summary' },
        { key: 'ipd_doc_referral', label: 'Referral Certificate' },
        { key: 'ipd_doc_death', label: 'Death Certificate' },
        { key: 'ipd_doc_expense', label: 'Expense Certificate' },
      ],
    },
    { key: 'ipd_supplies', label: 'Indoor Supplies' },
    { key: 'ipd_supplies_available', label: 'Available Supplies' },
    { key: 'ipd_supplies_demand', label: 'Send Demand' },
    { key: 'ipd_supplies_replace', label: 'Request Replacement' },
    { key: 'ipd_report', label: 'Report' },
  ],
  patient_record: [
    { key: 'pr_search', label: 'Search Patient Profile' },
    { key: 'pr_all', label: 'All Patients' },
  ],
  pharmacy: [
    { key: 'ph_dashboard', label: 'Dashboard' },
    { key: 'ph_pos', label: 'Point of Sale' },
    { key: 'ph_items', label: 'Med / Item List' },
    {
      key: 'ph_inventory',
      label: 'Inventory',
      children: [
        { key: 'ph_inventory_stock_in', label: 'Stock-In' },
        { key: 'ph_inventory_overview', label: 'Stock Overview' },
        { key: 'ph_inventory_allocate', label: 'Allocate' },
        { key: 'ph_inventory_adjustment', label: 'Stock Adjustment' },
        { key: 'ph_inventory_history', label: 'Med/Item History' },
      ],
    },
    { key: 'ph_prescriptions', label: 'Prescriptions' },
    { key: 'ph_customers', label: 'Customers' },
    { key: 'ph_suppliers', label: 'Suppliers' },
    { key: 'ph_manufacturers', label: 'Manufacturers' },
    { key: 'ph_sale_purchase', label: 'Sale/Purchase Record' },
    { key: 'ph_tax', label: 'Tax Calculation' },
    { key: 'ph_reports', label: 'Reports' },
    { key: 'ph_cash_ledger', label: 'Cash Reconciliation Ledger' },
    { key: 'ph_users', label: 'Users' },
    { key: 'ph_settings', label: 'Settings' },
  ],
  pathology: [
    { key: 'lab_overview', label: 'Overview' },
    { key: 'lab_invoices', label: 'Invoices' },
    { key: 'lab_sample', label: 'Sample Collection' },
    { key: 'lab_upload', label: 'Upload Results' },
    { key: 'lab_supervision', label: 'Supervision' },
    { key: 'lab_concluded', label: 'Concluded Results' },
    { key: 'lab_patient_db', label: 'Patient Database' },
    { key: 'lab_inventory', label: 'Inventory Management' },
    { key: 'lab_critical', label: 'Critical Results' },
    { key: 'lab_finance', label: 'Finance Section' },
    { key: 'lab_investigations', label: 'Investigations' },
    { key: 'lab_centres', label: 'Collection centres' },
    { key: 'lab_settings', label: 'Settings' },
  ],
  radiology: [
    { key: 'rad_dashboard', label: 'Overview' },
    { key: 'rad_orders', label: 'Order Investigation' },
    { key: 'rad_queue', label: 'Queue Management' },
    { key: 'rad_studies', label: 'Completed Studies' },
    { key: 'rad_reporting', label: 'Reporting' },
    { key: 'rad_verification', label: 'Supervision' },
    { key: 'rad_completed', label: 'Final Reports' },
    { key: 'rad_history', label: 'Patient Record' },
    { key: 'rad_critical', label: 'Critical Results' },
    { key: 'rad_inventory', label: 'Inventory Management', disabled: true },
    { key: 'rad_cash_reconciliation', label: 'Radiology Cash Reconciliation' },
    { key: 'rad_finance', label: 'Finance Section', disabled: true },
    { key: 'rad_settings', label: 'Settings' },
  ],
  dentistry: [
    { key: 'den_patients', label: 'Dental Patients' },
    { key: 'den_procedures', label: 'Procedures' },
    { key: 'den_followups', label: 'Follow-Ups' },
  ],
  blood_bank: [
    { key: 'bb_donors', label: 'Donor Registry' },
    { key: 'bb_inventory', label: 'Blood Inventory' },
    { key: 'bb_requests', label: 'Blood Requests' },
  ],
  operation_theatre: [
    { key: 'ot_overview', label: 'Overview' },
    { key: 'ot_cases', label: 'Cases list' },
    { key: 'ot_equipment', label: 'Equipments' },
    { key: 'ot_cleaning', label: 'Cleaning & Infection Control' },
    { key: 'ot_manage', label: 'Manage Theatres' },
  ],
  hdu_icu: [
    { key: 'icu_overview', label: 'Overview' },
    { key: 'icu_workflow', label: 'Indoor Workflow' },
    { key: 'icu_supplies', label: 'Indoor Supplies' },
    { key: 'icu_report', label: 'Report' },
  ],
  admin: [
    {
      key: 'hr',
      label: 'HR Management',
      children: [
        { key: 'hr_overview', label: 'Overview' },
        { key: 'hr_doctors', label: 'Doctors' },
        { key: 'hr_paramedical', label: 'Paramedical staff' },
      ],
    },
    {
      key: 'services',
      label: 'Services Management',
      children: [
        { key: 'services_overview', label: 'Overview' },
        { key: 'services_add', label: 'Add Service' },
        { key: 'services_edit', label: 'Edit Service' },
        { key: 'services_categories', label: 'Service Categories' },
      ],
    },
    {
      key: 'pathology',
      label: 'Pathology Management',
      children: [
        { key: 'pathology_investigations', label: 'Investigations List' },
        { key: 'pathology_misc', label: 'Misc Settings' },
      ],
    },
    { key: 'company', label: "Company's Profile" },
    { key: 'templates', label: 'Design Templates' },
  ],
};

export default { APP_SIDEBAR, MODULE_SUBMENUS };
